import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    User, Key, Shield, Save, Eye, EyeOff, Copy, Check,
    QrCode, Smartphone, AlertTriangle, CheckCircle2, Loader2
} from 'lucide-react';
import { apiMethods } from '@/services/api';
import toast from 'react-hot-toast';

const AccountSettings = () => {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('profile');
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [qrCodeData, setQrCodeData] = useState(null);
    const [copied, setCopied] = useState(false);

    // Form states
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [twoFAForm, setTwoFAForm] = useState({
        token: '',
        password: ''
    });

    // Fetch admin profile
    const { data: profile, isLoading } = useQuery({
        queryKey: ['adminProfile'],
        queryFn: async () => {
            const response = await apiMethods.admin.getProfile();
            return response.data.admin;
        }
    });

    // Change password mutation
    const changePasswordMutation = useMutation({
        mutationFn: (data) => apiMethods.admin.changePassword(data),
        onSuccess: () => {
            toast.success('Password changed successfully');
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to change password');
        }
    });

    // Setup 2FA mutation
    const setup2FAMutation = useMutation({
        mutationFn: () => apiMethods.admin.setup2FA(),
        onSuccess: (response) => {
            setQrCodeData(response.data);
            toast.success('2FA setup initiated. Scan the QR code with your authenticator app.');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to setup 2FA');
        }
    });

    // Verify 2FA mutation
    const verify2FAMutation = useMutation({
        mutationFn: (data) => apiMethods.admin.verify2FA(data),
        onSuccess: () => {
            toast.success('2FA enabled successfully');
            setQrCodeData(null);
            setTwoFAForm({ token: '', password: '' });
            queryClient.invalidateQueries(['adminProfile']);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to verify 2FA');
        }
    });

    // Disable 2FA mutation
    const disable2FAMutation = useMutation({
        mutationFn: (data) => apiMethods.admin.disable2FA(data),
        onSuccess: () => {
            toast.success('2FA disabled successfully');
            setTwoFAForm({ token: '', password: '' });
            queryClient.invalidateQueries(['adminProfile']);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to disable 2FA');
        }
    });

    const handlePasswordSubmit = (e) => {
        e.preventDefault();

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        if (passwordForm.newPassword.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        changePasswordMutation.mutate(passwordForm);
    };

    const handleSetup2FA = () => {
        setup2FAMutation.mutate();
    };

    const handleVerify2FA = (e) => {
        e.preventDefault();

        if (!twoFAForm.token) {
            toast.error('Please enter the verification code');
            return;
        }

        verify2FAMutation.mutate({ token: twoFAForm.token });
    };

    const handleDisable2FA = (e) => {
        e.preventDefault();

        if (!twoFAForm.password) {
            toast.error('Please enter your password');
            return;
        }

        const data = { password: twoFAForm.password };
        if (twoFAForm.token) {
            data.token = twoFAForm.token;
        }

        disable2FAMutation.mutate(data);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Copied to clipboard');
    };

    const togglePasswordVisibility = (field) => {
        setShowPassword(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    const tabs = [
        { id: 'profile', name: 'Profile', icon: User },
        { id: 'password', name: 'Change Password', icon: Key },
        { id: '2fa', name: 'Two-Factor Auth', icon: Shield }
    ];

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
                <p className="text-gray-600 mt-2">Manage your admin account security and preferences</p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-8">
                <nav className="-mb-px flex space-x-8">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                                    activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                <Icon className="h-5 w-5 mr-2" />
                                {tab.name}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Profile Tab */}
            {activeTab === 'profile' && (
                <div className="bg-white shadow-sm rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Information</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                            <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{profile?.email}</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                            <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{profile?.name}</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                            <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md capitalize">{profile?.role}</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Last Login</label>
                            <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                                {profile?.lastLogin ? new Date(profile.lastLogin).toLocaleString() : 'Never'}
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account Created</label>
                            <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                                {new Date(profile?.createdAt).toLocaleString()}
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Two-Factor Authentication</label>
                            <div className="flex items-center">
                                {profile?.twoFactorEnabled ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        <CheckCircle2 className="h-4 w-4 mr-1" />
                                        Enabled
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        <AlertTriangle className="h-4 w-4 mr-1" />
                                        Disabled
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Password Tab */}
            {activeTab === 'password' && (
                <div className="bg-white shadow-sm rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Change Password</h2>

                    <form onSubmit={handlePasswordSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                            <div className="relative">
                                <input
                                    type={showPassword.current ? 'text' : 'password'}
                                    value={passwordForm.currentPassword}
                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility('current')}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {showPassword.current ? (
                                        <EyeOff className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                            <div className="relative">
                                <input
                                    type={showPassword.new ? 'text' : 'password'}
                                    value={passwordForm.newPassword}
                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                                    required
                                    minLength={8}
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility('new')}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {showPassword.new ? (
                                        <EyeOff className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400" />
                                    )}
                                </button>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">Must be at least 8 characters long</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                            <div className="relative">
                                <input
                                    type={showPassword.confirm ? 'text' : 'password'}
                                    value={passwordForm.confirmPassword}
                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility('confirm')}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {showPassword.confirm ? (
                                        <EyeOff className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={changePasswordMutation.isPending}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
                        >
                            {changePasswordMutation.isPending ? (
                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            ) : (
                                <Save className="h-5 w-5 mr-2" />
                            )}
                            Change Password
                        </button>
                    </form>
                </div>
            )}

            {/* Two-Factor Authentication Tab */}
            {activeTab === '2fa' && (
                <div className="bg-white shadow-sm rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Two-Factor Authentication</h2>

                    {profile?.twoFactorEnabled ? (
                        <div className="space-y-6">
                            <div className="bg-green-50 border border-green-200 rounded-md p-4">
                                <div className="flex items-center">
                                    <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
                                    <span className="text-green-800 font-medium">Two-Factor Authentication is enabled</span>
                                </div>
                                <p className="text-green-700 mt-2">Your account is protected with 2FA.</p>
                            </div>

                            <form onSubmit={handleDisable2FA} className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900">Disable Two-Factor Authentication</h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Password</label>
                                    <input
                                        type="password"
                                        value={twoFAForm.password}
                                        onChange={(e) => setTwoFAForm(prev => ({ ...prev, password: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Authentication Code (optional)</label>
                                    <input
                                        type="text"
                                        value={twoFAForm.token}
                                        onChange={(e) => setTwoFAForm(prev => ({ ...prev, token: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter 6-digit code from your authenticator app"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={disable2FAMutation.isPending}
                                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 flex items-center"
                                >
                                    {disable2FAMutation.isPending ? (
                                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                    ) : (
                                        <Shield className="h-5 w-5 mr-2" />
                                    )}
                                    Disable 2FA
                                </button>
                            </form>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {!qrCodeData ? (
                                <div className="space-y-4">
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                                        <div className="flex items-center">
                                            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                                            <span className="text-yellow-800 font-medium">Two-Factor Authentication is disabled</span>
                                        </div>
                                        <p className="text-yellow-700 mt-2">Enable 2FA to add an extra layer of security to your account.</p>
                                    </div>

                                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                                        <h3 className="text-blue-800 font-medium mb-2">How it works:</h3>
                                        <ol className="text-blue-700 text-sm space-y-1 list-decimal list-inside">
                                            <li>Install an authenticator app like Google Authenticator or Authy</li>
                                            <li>Scan the QR code with your authenticator app</li>
                                            <li>Enter the verification code to complete setup</li>
                                        </ol>
                                    </div>

                                    <button
                                        onClick={handleSetup2FA}
                                        disabled={setup2FAMutation.isPending}
                                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
                                    >
                                        {setup2FAMutation.isPending ? (
                                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                        ) : (
                                            <Smartphone className="h-5 w-5 mr-2" />
                                        )}
                                        Setup Two-Factor Authentication
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="text-center">
                                        <h3 className="text-lg font-medium text-gray-900 mb-4">Scan QR Code</h3>
                                        <div className="inline-block p-4 bg-white border-2 border-gray-300 rounded-lg">
                                            <img src={qrCodeData.qrCode} alt="2FA QR Code" className="w-48 h-48" />
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 p-4 rounded-md">
                                        <p className="text-sm text-gray-700 mb-2">Manual entry key:</p>
                                        <div className="flex items-center space-x-2">
                                            <code className="flex-1 bg-white p-2 border rounded text-sm font-mono">
                                                {qrCodeData.manualEntryKey}
                                            </code>
                                            <button
                                                onClick={() => copyToClipboard(qrCodeData.manualEntryKey)}
                                                className="p-2 text-gray-500 hover:text-gray-700"
                                            >
                                                {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </div>

                                    <form onSubmit={handleVerify2FA} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Enter verification code from your authenticator app
                                            </label>
                                            <input
                                                type="text"
                                                value={twoFAForm.token}
                                                onChange={(e) => setTwoFAForm(prev => ({ ...prev, token: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Enter 6-digit code"
                                                maxLength={6}
                                                required
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={verify2FAMutation.isPending}
                                            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 flex items-center"
                                        >
                                            {verify2FAMutation.isPending ? (
                                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                            ) : (
                                                <CheckCircle2 className="h-5 w-5 mr-2" />
                                            )}
                                            Enable 2FA
                                        </button>
                                    </form>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AccountSettings;